import { t, Instance, flow } from 'mobx-state-tree';
import {
  ActivityDigestCadence,
  notificationsApi,
  type ActivityNotificationsSummaryResponse,
  type UpdateNotificationPreferencesBody
} from '@/api/notificationsWrapper';

const CADENCES = [
  ActivityDigestCadence.Off,
  ActivityDigestCadence.Daily,
  ActivityDigestCadence.Weekly,
  ActivityDigestCadence.Monthly
] as const;

const NotificationPreferencesModel = t.model('NotificationPreferences', {
  activityDigest: t.enumeration('ActivityDigestCadence', [...CADENCES]),
  activityDigestEnabled: t.boolean,
  productUpdatesEnabled: t.boolean,
  lastSeenChangelogVersion: t.maybe(t.string),
  lastSeenActivityAt: t.maybeNull(t.string),
  suggestionReviewEnabled: t.boolean,
  accessRequestEnabled: t.boolean,
  timezone: t.string,
  updatedAt: t.string
});

function mapPrefs(data: {
  activityDigest: string;
  activityDigestEnabled: boolean;
  productUpdatesEnabled: boolean;
  lastSeenChangelogVersion?: string;
  lastSeenActivityAt?: string | null;
  suggestionReviewEnabled: boolean;
  accessRequestEnabled: boolean;
  timezone: string;
  updatedAt: string;
}) {
  return {
    activityDigest: data.activityDigest as (typeof CADENCES)[number],
    activityDigestEnabled: data.activityDigestEnabled,
    productUpdatesEnabled: data.productUpdatesEnabled,
    lastSeenChangelogVersion: data.lastSeenChangelogVersion,
    lastSeenActivityAt: data.lastSeenActivityAt ?? null,
    suggestionReviewEnabled: data.suggestionReviewEnabled,
    accessRequestEnabled: data.accessRequestEnabled,
    timezone: data.timezone,
    updatedAt: data.updatedAt
  };
}

export const NotificationPreferencesStore = t
  .model('NotificationPreferencesStore', {
    preferences: t.maybe(NotificationPreferencesModel),
    /** Frozen watermark for Activity screen row dots for the current visit */
    activityViewWatermark: t.maybeNull(t.string),
    unreadCount: t.optional(t.number, 0),
    /** Per-family unread counts for My Families cards */
    unreadByFamily: t.map(t.number),
    isLoading: t.optional(t.boolean, false),
    isSaving: t.optional(t.boolean, false),
    isLoadingSummary: t.optional(t.boolean, false),
    error: t.maybeNull(t.string)
  })
  .views((self) => ({
    get hasUnread(): boolean {
      return self.unreadCount > 0;
    },

    unreadForFamily(familyId: string): number {
      return self.unreadByFamily.get(familyId) ?? 0;
    },

    /**
     * Whether an activity row should show the "new" dot.
     * Own actions never count as new (viewer already knows they made them).
     */
    isActivityNew(
      createdAt: string,
      mode: 'visit' | 'global' = 'global',
      actorUserId?: string | null,
      currentUserId?: string | null
    ): boolean {
      if (currentUserId && actorUserId && actorUserId === currentUserId) {
        return false;
      }
      const watermark =
        mode === 'visit'
          ? (self.activityViewWatermark ?? self.preferences?.lastSeenActivityAt ?? null)
          : (self.preferences?.lastSeenActivityAt ?? null);
      if (!watermark) {
        // No watermark yet: treat last 7 days as new (aligned with API default lookback for badge)
        const lookbackMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
        return new Date(createdAt).getTime() > lookbackMs;
      }
      return new Date(createdAt).getTime() > new Date(watermark).getTime();
    }
  }))
  .actions((self) => {
    const applySummary = (data: ActivityNotificationsSummaryResponse) => {
      self.unreadCount = data.unreadCount ?? 0;
      self.unreadByFamily.clear();
      for (const row of data.byFamily ?? []) {
        if (row.familyId && row.unreadCount > 0) {
          self.unreadByFamily.set(row.familyId, row.unreadCount);
        }
      }
      if (self.preferences && data.lastSeenActivityAt !== undefined) {
        self.preferences.lastSeenActivityAt = data.lastSeenActivityAt ?? null;
      }
    };

    return {
      clearError() {
        self.error = null;
      },

      loadPreferences: flow(function* () {
        self.isLoading = true;
        self.error = null;
        try {
          const result = yield notificationsApi.getPreferences();
          if (!result.success) {
            self.error = result.error?.message ?? 'Failed to load notification preferences';
            return;
          }
          self.preferences = mapPrefs(result.data);
        } catch (error) {
          self.error = error instanceof Error ? error.message : 'Failed to load notification preferences';
        } finally {
          self.isLoading = false;
        }
      }),

      updatePreferences: flow(function* (body: UpdateNotificationPreferencesBody) {
        self.isSaving = true;
        self.error = null;
        try {
          const result = yield notificationsApi.updatePreferences(body);
          if (!result.success) {
            self.error = result.error?.message ?? 'Failed to update notification preferences';
            return false;
          }
          self.preferences = mapPrefs(result.data);
          return true;
        } catch (error) {
          self.error = error instanceof Error ? error.message : 'Failed to update notification preferences';
          return false;
        } finally {
          self.isSaving = false;
        }
      }),

      loadUnreadSummary: flow(function* () {
        self.isLoadingSummary = true;
        try {
          const result = yield notificationsApi.getActivityNotificationsSummary();
          if (!result.success) {
            // Keep prior count; surface quietly for debugging
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn('loadUnreadSummary failed', result.error?.message);
            }
            return;
          }
          applySummary(result.data);
        } finally {
          self.isLoadingSummary = false;
        }
      }),

      /**
       * Freeze watermark for row dots, then advance server watermark (clears AppBar badge).
       */
      markActivitySeenOnVisit: flow(function* () {
        if (!self.preferences) {
          const prefsResult = yield notificationsApi.getPreferences();
          if (prefsResult.success) {
            self.preferences = mapPrefs(prefsResult.data);
          }
        }
        self.activityViewWatermark = self.preferences?.lastSeenActivityAt ?? null;

        const result = yield notificationsApi.markActivityNotificationsSeen();
        if (!result.success) return;
        applySummary({
          unreadCount: 0,
          byFamily: [],
          lastSeenActivityAt: result.data.lastSeenActivityAt ?? new Date().toISOString()
        });
      })
    };
  });

export type NotificationPreferencesStoreType = Instance<typeof NotificationPreferencesStore>;

export const notificationPreferencesStore = NotificationPreferencesStore.create({});
